import { useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Form, FormikProvider, useFormik } from 'formik';
import { toFormikValidationSchema, toFormikValidate } from 'zod-formik-adapter';
import { ObservableKind } from '@/pages/configure/workspace/_partials/ObservableKinds';
import { useOnKeyDown } from '@/utils/util';
import {
  OBSERVABLE_KIND_TYPE_OPTIONS,
  ObservableKindSchema,
} from '@/zod/schemas/workspaceConfig/ObservableKind';
import {
  FormDropdown,
  FormError,
  FormInput,
  FormMessage,
} from '@/components/form/form';

export type ObservableKindDialogProps = {
  onSave: (kind: ObservableKind) => void;
  kind: ObservableKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEdit: boolean;
  existingKinds: string[];
};

export const ObservableKindDialog = ({
  onSave,
  kind,
  open,
  onOpenChange,
  isEdit,
  existingKinds,
}: ObservableKindDialogProps) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const formik = useFormik({
    initialValues: {
      name: kind.name,
      sqlType: kind.sqlType,
    },
    validationSchema: toFormikValidationSchema(ObservableKindSchema),
    validate: toFormikValidate(ObservableKindSchema),
    onSubmit: (values, { resetForm }) => {
      onSave({ ...values });
      resetForm();
    },
  });

  const { values, handleChange, handleBlur } = formik;

  const { handleKeyDown } = useOnKeyDown({
    enabled: !!formik.values.name && !!formik.values.sqlType,
    onEnter: () => formik.submitForm(),
  });

  const onHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    const value = e.target.value;

    const isDuplicate = existingKinds.some(
      (k) => k.toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate) {
      setError(`Kind "${value}" already exists`);
      return;
    }

    setError(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit && 'Edit observable kind'}
            {!isEdit && 'Add observable kind'}
          </DialogTitle>
        </DialogHeader>
        <FormikProvider value={formik}>
          <Form>
            <div>
              <Label htmlFor="name" id="name-label" required>
                Observable
              </Label>
              <FormInput
                id="name"
                name="name"
                value={values.name}
                onChange={onHandleChange}
                onBlur={handleBlur}
                autoComplete="off"
                onKeyDown={handleKeyDown}
                aria-labelledby="name-label"
              />
              <FormError name="name" />
              {error && (
                <FormMessage
                  type="error"
                  className="mt-2"
                  data-testid="form-error-detection-category"
                >
                  {error}
                </FormMessage>
              )}

              <Spacer size="lg" />

              <Label htmlFor="sqlType" id="sqlType-label" required>
                SQL type
              </Label>
              <FormDropdown
                options={[...OBSERVABLE_KIND_TYPE_OPTIONS]}
                value={values.sqlType}
                onChange={handleChange}
                onBlur={handleBlur}
                name="sqlType"
                maxHeight={200}
              />
              <FormError name="sqlType" />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="submit"
                variant="primary"
                data-testid="save-observable-kind-button"
                disabled={!!error}
              >
                Save
              </Button>
            </div>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
};
